// Lib
import gql from 'graphql-tag';
import {
  AUTH,
  AUTH_ANALYTIC,
  AUTH_CAMPAIGNS,
  AUTH_GA_METRICS,
  CAMPAIGN,
  CAMPAIGN_AUTH,
  CAMPAIGN_AUTH_ANALYTICS_FLAT,
  CAMPAIGN_AUTH_LIGHT,
  CAMPAIGN_AUTH_TASK,
  CAMPAIGN_AUTH_TASK_LIGHT,
  CAMPAIGN_LIST_ITEM,
  CAMPAIGN_MINIMAL,
  CAMPAIGN_PAYMENT,
  CAMPAIGN_PAYOUT,
  POST,
} from '../types';

export const campaignPaymentQuery = gql`
  query Campaign($input: CampaignSingleInput) {
    campaign(input: $input) {
       payouts {
          ${CAMPAIGN_PAYOUT}
        }
        payments {
          ${CAMPAIGN_PAYMENT}
        }
    }
  }
`;

export const campaignQuery = gql`
  query Campaign($input: CampaignSingleInput) {
    campaign(input: $input) {
      ${CAMPAIGN}
    }
  }
`;

// Lightweight status probe used by the Campaign route to pick the detail
// view (HISTORICAL → read-only imported overview, else brand/creator).
export const campaignStatusQuery = gql`
  query CampaignStatus($input: CampaignSingleInput) {
    campaign(input: $input) {
      _id
      status
    }
  }
`;

// Same probe plus the viewer's relation (OWNER / PARTICIPANT / NONE), which
// picks the brand vs creator view per campaign. Older backends reject the
// field — callers fall back to `campaignStatusQuery` + org type.
export const campaignStatusWithRelationQuery = gql`
  query CampaignStatusWithRelation($input: CampaignSingleInput) {
    campaign(input: $input) {
      _id
      status
      viewerRelation
    }
  }
`;

export const campaignList = gql`
  query CampaignList($input: CampaignListInput) {
    campaignList(input: $input) {
      hasMore
      cursor
      items {
        ${CAMPAIGN}
      }
    }
  }
`;

// Lightweight query for campaign list page (useBrandCampaigns)
export const campaignListLight = gql`
  query CampaignListLight($input: CampaignListInput) {
    campaignList(input: $input) {
      hasMore
      cursor
      items {
        ${CAMPAIGN_LIST_ITEM}
      }
    }
  }
`;

// Minimal query for campaign selection dropdown (useAddToCampaignButton)
export const campaignListMinimal = gql`
  query CampaignListMinimal($input: CampaignListInput) {
    campaignList(input: $input) {
      hasMore
      cursor
      items {
        ${CAMPAIGN_MINIMAL}
      }
    }
  }
`;

export const campaignAuth = gql`
  query CampaignAuth($input: CampaignAuthInput!, $campaignId: String!) {
    campaignAuth(input: $input) {
      hasMore
      items {
        ${CAMPAIGN_AUTH}
      }
      cursor
    }
  }
`;

// Optimized query - fetches only fields that are actually used in UI
// Removes ~50% of unused fields for better performance
export const campaignAuthLight = gql`
  query CampaignAuthLight($input: CampaignAuthInput!, $campaignId: String!) {
    campaignAuth(input: $input) {
      hasMore
      items {
        ${CAMPAIGN_AUTH_LIGHT}
      }
      cursor
    }
  }
`;

export const authSearch = gql`
  query AuthSearch($input: AuthSearchInput) {
    authSearch(input: $input) {
      cursor
      hasMore
      items {
        ${AUTH}
      }
    }
  }
`;

export const campaignPost = gql`
  query CampaignPost($input: CampaignPostInput!) {
    campaignPost(input: $input) {
      items {
        ${POST}
      }
      hasMore
      cursor
    }
  }
`;

// #713 / sweep (#706): every account that has posted matching content for the
// campaign — members and non-member "discovered" posters alike. `inCampaign`
// flags whether the author is already an influencer on the campaign; the UI
// offers an "Add to campaign" affordance for the ones that aren't.
export const campaignAllInfluencers = gql`
  query CampaignAllInfluencers($input: CampaignAllInfluencersInput!) {
    campaignAllInfluencers(input: $input) {
      items {
        authId
        inCampaign
        auth {
          _id
          username
          fullName
          profileUrl
          followersCount
          avatar {
            url
          }
        }
      }
    }
  }
`;

// #713 / sweep (#706): on-demand status probe for the campaign hashtag/mention
// sweep job. The FE polls this every few seconds while `state == RUNNING`.
export const campaignSweepStatus = gql`
  query CampaignSweepStatus($input: CampaignSweepStatusInput!) {
    campaignSweepStatus(input: $input) {
      campaignId
      state
      startedAt
      finishedAt
      message
      cooldownSecondsRemaining
      stats {
        scanned
        discoveredNew
        postsAttributed
        membersHit
        refreshed
        pagesFetched
        capped
      }
    }
  }
`;

export const campaignExport = gql`
  query CampaignExport($input: CampaignExportAuthInput) {
    campaignExport(input: $input) {
      authCsv
      postCsv
    }
  }
`;

export const campaignAuthTaskTimeline = gql`
  query CampaignAuthTaskTimeline($input: CampaignAuthTaskListInput!) {
    campaignAuthTaskList(input: $input) {
      items {
        _id
        activityTimeline {
          actorId
          actorType
          createdAt
          eventType
          payload
          sequence
        }
      }
    }
  }
`;

export const campaignAuthTaskInviteMessageGenerate = gql`
  query CampaignAuthTaskInviteMessageGenerate(
    $input: CampaignAuthTaskInviteMessageGenerateInput
  ) {
    campaignAuthTaskInviteMessageGenerate(input: $input)
  }
`;

export const campaignAuthAnalyticsHistory = gql`
  query CampaignAuthAnalyticsHistory($input: CampaignAuthAnalyticsHistoryListInput) {
    campaignAuthAnalyticsHistory(input: $input) {
      limit
      skip
      hasMore
      items {
        ${AUTH_ANALYTIC}
      }
      cacheKey,
    }
  }
`;

export const organizationCampaignAuthTaskList = gql`
  query OrganizationCampaignAuthTaskList($input: OrganizationCampaignAuthTaskListInput) {
    organizationCampaignAuthTaskList(input: $input) {
      cursor
      hasMore
      items {
        ${CAMPAIGN_AUTH_TASK}
      }
    }
  }
`;

export const authById = gql`
  query AuthById($input: AuthByIdInput) {
    authById(input: $input) {
      ${AUTH}
    }
  }
`;

export const authCampaigns = gql`
  query AuthCampaigns($input: AuthByIdInput) {
    authById(input: $input) {
      _id
      campaigns {
        ${AUTH_CAMPAIGNS}
      }
    }
  }
`;

// One creator's campaign-scoped task + analytics — powers Deal/Payments in the
// general (non-campaign) drawer once a campaign is picked. Same campaign-scoped
// fields the campaignAuth list carries, but for a single (auth, campaign) pair.
export const authCampaignTask = gql`
  query AuthCampaignTask($input: AuthByIdInput, $campaignId: String!) {
    authById(input: $input) {
      _id
      campaignTasks(campaignId: $campaignId) {
        ${CAMPAIGN_AUTH_TASK_LIGHT}
      }
      campaignAuthAnalyticsLatest(campaignId: $campaignId) {
        ${CAMPAIGN_AUTH_ANALYTICS_FLAT}
      }
    }
  }
`;

export const campaignAuthGaMetrics = gql`
  query CampaignAuthGaMetrics($input: CampaignAuthGaMetricsListInput) {
    campaignAuthGaMetrics(input: $input) {
      limit
      skip
      hasMore
      items {
        ${AUTH_GA_METRICS}
      }
      cacheKey,
    }
  }
`;

export const campaignAnalyticsQuery = gql`
  query CampaignAnalytics($input: CampaignAnalyticsInput!) {
    campaignAnalytics(input: $input) {
      campaignId
      startDate
      endDate
      offeredMoney
      committedBudget
      spent
      roi
      emv
      roas
      revenue
      cpv
      cpe
      cpo
      postsCount
      storiesCount
      videoCount
      likes
      comments
      shares
      views
      engagement
      engagementRate
      linkClicks
      totalFollowers
      gaClicks
      gaPageviews
      gaRevenue
      gaPurchases
      gaSessions
    }
  }
`;

export const campaignAnalyticsHistoryQuery = gql`
  query CampaignAnalyticsHistory($input: CampaignAnalyticsHistoryInput!) {
    campaignAnalyticsHistory(input: $input) {
      limit
      cursor
      hasMore
      items {
        authId
        snapshotDate
        likesCumulative
        commentsCumulative
        sharesCumulative
        viewsCumulative
        engagementCumulative
        engagementRate
        followersCount
        postsCountCumulative
        storiesCountCumulative
        videoCountCumulative
        linkClicks
        gaClicksCumulative
        gaPageviewsCumulative
        gaPurchasesCumulative
        gaRevenueCumulative
        gaSessionsCumulative
        emvCumulative
        committedBudget
        cpe
        cpv
        offeredMoney
        roas
        roi
        spent
        revenue
      }
    }
  }
`;
